import * as Phaser from "phaser";
import { BasicScene } from "$/game/prefabs/scenes/BasicScene.ts";
import { PetGameObject } from "$/game/prefabs/PetGameObject.ts";
import { BowlInteractiveObject } from "$/game/prefabs/home/BowlInteractiveObject.ts";
import { FoodItemObject } from "$/game/prefabs/home/FoodItemObject.ts";
import { petActions } from "$/game/emitters.ts";

export class FoodPetSystem {
  private listOfFoods: FoodItemObject[] = [];

  constructor(
    private myScene: BasicScene,
    private pet: PetGameObject,
    private bowl: BowlInteractiveObject,
  ) {
    this.bowl.setOnClickCallback(() => {
      this.spawnFood();
    });
  }

  private spawnFood(): void {
    // Проверяем, есть ли еда в запасе
    const petData = this.myScene.storage.pet();
    const availableFood = (petData?.food ?? 0) - this.listOfFoods.length;

    if (availableFood <= 0) {
      // Нет еды - вибрация ошибки и выход
      if (this.myScene.settings.getSetting("isVibrationEnabled")) {
        this.myScene.telegram.twaSDK.hapticFeedback.notificationOccurred(
          "error",
        );
      }
      return;
    }

    const food = new FoodItemObject(
      this.myScene,
      this.bowl.x,
      this.bowl.y - 100,
    );

    // Отстреливаем еду вверх с небольшим разбросом
    const config =
      this.myScene.global.TUNER_TWA_CONFIG.game.systems.food.throwForce;
    const randomHorizontal = Phaser.Math.FloatBetween(
      config.horizontal.min,
      config.horizontal.max,
    );
    food.setVelocity(randomHorizontal, config.upward);

    this.myScene.add.existing(food);
    this.listOfFoods.push(food);

    // Подписываемся на событие отпускания еды
    food.setOnReleaseCallback(() => {
      return this.handleFoodRelease(food);
    });
  }

  private handleFoodRelease(food: FoodItemObject): boolean {
    // Проверяем, находится ли еда в области пета
    const isOverPet = this.isFoodOverPet(food);

    if (isOverPet) {
      // Удаляем еду из списка
      const index = this.listOfFoods.indexOf(food);
      if (index > -1) {
        this.listOfFoods.splice(index, 1);
      }

      // Уничтожаем еду
      food.destroy();

      // Вызываем API для кормления
      this.feedPet();

      petActions.foodEaten();

      if (this.myScene.settings.getSetting("isVibrationEnabled")) {
        this.myScene.telegram.twaSDK.hapticFeedback.impactOccurred("medium");
      }

      this.myScene.soundManager.playSound(
        this.myScene.keys.assets.sounds.feedSuccess,
      );

      return true; // Возвращаем true, чтобы предотвратить вызов throwFood
    }

    return false; // Еда не была уничтожена, можно бросать
  }

  private async feedPet(): Promise<void> {
    try {
      await this.myScene.api.pet.feed();
    } catch (error) {
      console.error("Error feeding pet:", error);
    }
  }

  private isFoodOverPet(food: FoodItemObject): boolean {
    // Получаем границы объектов
    const foodBounds = food.getBounds();
    const petBounds = this.pet.getBounds();

    // Проверяем пересечение прямоугольников
    const overlaps = Phaser.Geom.Rectangle.Overlaps(foodBounds, petBounds);

    // Дополнительная проверка: расстояние между центрами
    const distance = Phaser.Math.Distance.Between(
      food.x,
      food.y,
      this.pet.x,
      this.pet.y,
    );
    const maxDistance = (food.width + this.pet.width) / 2;
    const isClose = distance < maxDistance;

    return overlaps || isClose;
  }
}
